import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * SiteContent provides editor-overridable copy & images across the salon.
 * Use the `useContent(key, default)` hook in any component to read a value.
 * Editor's Workshop in /founder writes values via PUT /api/site-content/:key.
 */
const SiteContentCtx = createContext({
  content: {},
  refresh: () => {},
});

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/site-content");
      setContent(data || {});
    } catch {
      // silent — fall back to defaults
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh whenever the tab comes back into focus, so a change made on
  // one device (e.g. phone) is picked up on the other (e.g. desktop) as
  // soon as the user returns to the tab. No hard refresh needed.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onFocus = () => refresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  return (
    <SiteContentCtx.Provider value={{ content, refresh }}>
      {children}
    </SiteContentCtx.Provider>
  );
};

export const useSiteContent = () => useContext(SiteContentCtx);

export const useContent = (key, defaultValue = "") => {
  const { content } = useSiteContent();
  const v = content[key];
  if (v === undefined || v === null || v === "") return defaultValue;
  return v;
};
